import { useEffect, useRef } from 'preact/hooks';
import { addEventEmojisToCalendar, updateEventBadgesForVisibleMonth } from '../utils/pickerHelpers';

/**
 * Hook to handle calendar updates (emulates componentDidUpdate logic)
 * Manages updates when props change after initial mount
 * 
 * @param {Object} params
 * @param {Object} params.inputRef - Ref to the input element
 * @param {Object} params.config - Update configuration
 * @param {string} params.config.field - Field name
 * @param {boolean} params.config.calendarOpen - Calendar open flag
 * @param {boolean} params.config.autoOpenCalendar - Auto open calendar flag
 * @param {string} params.config.date - Current date value
 * @param {Object} params.actions - Control actions
 * @param {Function} params.actions.setDate - Function to set date
 * @param {Function} params.actions.setOpenTicketBtn - Function to set open ticket button
 * @param {Function} params.actions.setDatePickerText - Function to set date picker text
 * @param {Array} params.events - Calendar events to display on days (optional)
 * @param {Date} params.currentMonth - Current visible month (triggers badge/emoji updates when changed)
 * @param {Object} params.dictionary - Translation dictionary for event labels (from WidgetConfig context)
 */
const useCalendarUpdate = ({
  inputRef,
  config,
  actions,
  events = [],
  currentMonth = null,
  dictionary = null,
}) => {
  const {
    field,
    calendarOpen,
    autoOpenCalendar,
    date,
  } = config;

  const {
    setDate,
    setOpenTicketBtn,
    setDatePickerText,
  } = actions;

  // Store previous values to detect changes (like prevProps in componentDidUpdate)
  const prevDateRef = useRef(date);
  const prevAutoOpenCalendarRef = useRef(autoOpenCalendar);
  const prevCalendarOpenRef = useRef(calendarOpen);
  const prevEventsRef = useRef(events);
  const prevCurrentMonthRef = useRef(currentMonth);
  const eventBadgesInsertedRef = useRef(false);

  /**
   * Helper function to get picker root element
   * Works in both desktop (picker in parent) and mobile (picker moved to portal)
   * @param {jQuery} $input - jQuery input element
   * @returns {jQuery} jQuery element representing the picker root
   */
  const getPickerRoot = ($input) => {
    if (!$input) return null;
    const picker = $input.pickadate ? $input.pickadate('picker') : null;
    return picker && picker.$root ? picker.$root : $input.parent().find('.picker');
  };

  useEffect(() => {
    // Skip on initial mount - only run on updates
    if (!inputRef.current) return;

    const $input = inputRef.current;
    const prevDate = prevDateRef.current;
    const prevAutoOpenCalendar = prevAutoOpenCalendarRef.current;

    // If date changed from null/undefined to having a value
    if (date && !prevDate) {
      setDate(date);
    }

    // If autoOpenCalendar changes to true, open the calendar
    if (!prevAutoOpenCalendar && autoOpenCalendar) {
      $input?.click();
    }

    // Re-check and set open ticket button
    setOpenTicketBtn(field, calendarOpen);

    // Check and set date picker text if not exists
    // Use picker's $root to find text container (works in both desktop and mobile)
    const $pickerRoot = getPickerRoot($input);
    if ($pickerRoot && !$pickerRoot.find('.datePickerTextContainer').length) {
      setDatePickerText(field);
    }

    // Check if calendar just opened
    const prevCalendarOpen = prevCalendarOpenRef?.current ?? false;
    const calendarJustOpened = calendarOpen && !prevCalendarOpen;
    
    // Check if currentMonth changed (indicates month change)
    const prevCurrentMonth = prevCurrentMonthRef.current;
    const currentMonthChanged = currentMonth && prevCurrentMonth
      ? currentMonth.getFullYear() !== prevCurrentMonth.getFullYear() ||
        currentMonth.getMonth() !== prevCurrentMonth.getMonth()
      : currentMonth !== prevCurrentMonth; // One is null, the other isn't

    // Update events when they change
    const prevEvents = prevEventsRef.current;
    const eventsChanged = JSON.stringify(events) !== JSON.stringify(prevEvents);
    
    // Determine if we need to update emojis and badges
    let shouldUpdateEmojis = false;
    let eventsForBadges = null; // null means don't update, [] means remove, array means update with those events
    
    if (eventsChanged && events && events.length > 0) {
      // Events changed and we have events - update emojis and badges
      shouldUpdateEmojis = true;
      eventsForBadges = events;
    } else if (eventsChanged && (!events || events.length === 0)) {
      // Events changed but cleared - remove badges, no emojis to update
      eventsForBadges = [];
    } else if (currentMonthChanged && events && events.length > 0) {
      // Month changed but events are the same - update badges and emojis anyway
      // This handles the case where we navigate months but events don't change
      // because they're still within the 90-day window
      shouldUpdateEmojis = true;
      eventsForBadges = events;
    } else if (calendarJustOpened && events && events.length > 0) {
      // Calendar just opened and we have events - update emojis and badges
      // This handles the case when calendar opens with preloaded dates and events are available
      shouldUpdateEmojis = true;
      eventsForBadges = events;
    }

    // Update event emojis in calendar days if needed (all events, not filtered)
    if (shouldUpdateEmojis) {
      setTimeout(() => {
        addEventEmojisToCalendar({ $input, events });
      }, 0);
    }

    // Update badges based on visible month (only if we determined we need to)
    if (eventsForBadges !== null) {
      updateEventBadgesForVisibleMonth({
        $input,
        events: eventsForBadges,
        currentMonth,
        insertedRef: eventBadgesInsertedRef,
        dictionary, // Pass translation dictionary for event labels
      });
    }

    // Update refs for next render
    prevDateRef.current = date;
    prevAutoOpenCalendarRef.current = autoOpenCalendar;
    prevCalendarOpenRef.current = calendarOpen;
    prevEventsRef.current = events;
    prevCurrentMonthRef.current = currentMonth;
  }, [
    field,
    calendarOpen,
    autoOpenCalendar,
    date,
    events,
    currentMonth,
    dictionary,
    inputRef,
    setDate,
    setOpenTicketBtn,
    setDatePickerText,
  ]);
};

export default useCalendarUpdate;

